import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import {
  BusinessCenterOutlinedIcon,
  NoteAltOutlinedIcon,
  RequestQuoteOutlinedIcon,
} from '@cmg/design-system';
import { xcSelectors } from '@cmg/e2e-selectors';
import { useMemo } from 'react';

import { useFeatureToggles } from '../../../common/config';
import { useIsSmallScreen } from '../../../common/hooks/useIsSmallScreen';
import { useSupersededOfferingContext } from '../contexts/SupersededOfferingContext';
import offeringSidePanelBuySideRouteFactory from '../offeringSidePanelBuySideRouteFactory';
import { useOfferingSidePanelRouteMatch } from './useOfferingSidePanelMatch';
import { useSidePanelBuySideOnPlatformOrderNavItem } from './useSidePanelBuySideOnPlatformOrderNavItem';
import { NavItem, NavItemGroup } from './useSidePanelSellSideNavItems';

type Props = {
  baseSidePanelUrl: string;
  isExecutingOnPlatform: boolean;
  isLinkedOffering: boolean;
};

export function useSidePanelBuySideNavItems({
  baseSidePanelUrl,
  isExecutingOnPlatform,
  isLinkedOffering,
}: Props) {
  const { isFiledOffering } = useSupersededOfferingContext();
  const hasIoiReadPermission = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  const matchesOfferingDetails = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.offeringDetails.routePath
  );

  const matchesNotes = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.offeringNotes.routePath
  );

  const matchesSummary = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.offeringSummary.routePath
  );

  const matchesCommunicatedDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.communicatedDemand.routePath
  );

  const matchesInternalDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.internalDemand.routePath
  );

  const isSmallScreen = useIsSmallScreen();

  const onPlatformOrderNavItem = useSidePanelBuySideOnPlatformOrderNavItem({
    isFiledOffering,
    baseSidePanelUrl,
    isLinkedOffering,
  });

  return useMemo(() => {
    const navItems: Array<NavItem | NavItemGroup> = [];
    if (isSmallScreen) {
      navItems.push({
        id: 'summary',
        label: 'Summary',
        href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.offeringSummary.getUrlPath(),
        selected: matchesSummary,
      });
    }
    navItems.push({
      id: 'offering-details',
      label: 'Offering Details',
      href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.offeringDetails.getUrlPath(),
      selected: matchesOfferingDetails,
      icon: <BusinessCenterOutlinedIcon />,
    });
    if (
      hasIoiReadPermission &&
      (!isExecutingOnPlatform || !isOdsFunctionalityEnabled || isLinkedOffering)
    ) {
      navItems.push({
        id: 'notes',
        label: 'Notes',
        href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.offeringNotes.getUrlPath(),
        selected: matchesNotes,
        icon: <NoteAltOutlinedIcon />,
        testId: xcSelectors.offeringSidePanelOfferingNotesLink.testId,
      });
    }

    if (isExecutingOnPlatform) {
      navItems.push(onPlatformOrderNavItem);
    } else {
      const offPlatformOrderGroup: NavItemGroup = {
        id: 'order',
        label: 'Order',
        routePath: offeringSidePanelBuySideRouteFactory.orderBook.routePath,
        icon: <RequestQuoteOutlinedIcon />,
        href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.orderAllocation.getUrlPath(),
        testId: 'on-platform-order-book-link',
        items: [
          {
            id: 'communicated-demand',
            label: 'Communicated Demand',
            selected: matchesCommunicatedDemand,
            href:
              baseSidePanelUrl +
              offeringSidePanelBuySideRouteFactory.communicatedDemand.getUrlPath(),
          },
          {
            id: 'internal-demand',
            label: 'Internal Demand',
            selected: matchesInternalDemand,
            href:
              baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.internalDemand.getUrlPath(),
            testId: xcSelectors.offeringSidePanelInternalDemandLink.testId,
          },
        ],
      };

      navItems.push(offPlatformOrderGroup);
    }
    return navItems;
  }, [
    isSmallScreen,
    hasIoiReadPermission,
    isExecutingOnPlatform,
    matchesOfferingDetails,
    matchesNotes,
    matchesSummary,
    matchesCommunicatedDemand,
    matchesInternalDemand,
    isLinkedOffering,
    isOdsFunctionalityEnabled,
    baseSidePanelUrl,
    onPlatformOrderNavItem,
  ]);
}
